/**
 * iter228 · DailyProductionGrid
 *
 * SKU × Days Excel-style grid — rolling 7-day (default) planning sheet.
 * Same data source as the Rolling / Order-Book / Kanban views: computed
 * on the backend from db.factory_plans + db.dispatches.
 *
 * Colour semantics per cell:
 *   🔴 planned but not yet produced  (planned – produced > 0)
 *   🟢 produced (green)              (produced > 0)
 *   🔵 dispatched (blue)             (dispatched > 0)
 *
 * The row-level totals show the strict Red/Green/Blue quantities.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RotateCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { fmtQty } from '../../utils/format';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const dayLabel = (iso, todayIso) => {
  const d = new Date(iso + 'T00:00:00');
  const isToday = iso === todayIso;
  const wk = d.toLocaleDateString('en-IN', { weekday: 'short' });
  const dm = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  return { wk, dm, isToday };
};

export default function DailyProductionGrid() {
  const [data, setData] = useState({ rows: [], dates: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState(() => new Date().toISOString().slice(0, 10));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/factory-plans/grid/daily`, {
        params: { start, days: 7 }, withCredentials: true,
      });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load daily grid');
    } finally { setLoading(false); }
  }, [start]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const shiftStart = (n) => {
    const d = new Date(start + 'T00:00:00');
    d.setDate(d.getDate() + n);
    setStart(d.toISOString().slice(0, 10));
  };

  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-2 flex items-center gap-2 flex-wrap">
        <button onClick={() => shiftStart(-7)} data-testid="fp-daily-prev-week" className="text-zinc-400 hover:text-zinc-100 bg-zinc-800 hover:bg-zinc-700 rounded-lg px-2 py-1.5">
          <ChevronLeft size={13} />
        </button>
        <input type="date" value={start} onChange={e => setStart(e.target.value)} data-testid="fp-daily-start-date"
          className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-zinc-100" />
        <button onClick={() => shiftStart(7)} data-testid="fp-daily-next-week" className="text-zinc-400 hover:text-zinc-100 bg-zinc-800 hover:bg-zinc-700 rounded-lg px-2 py-1.5">
          <ChevronRight size={13} />
        </button>
        <button onClick={() => setStart(todayIso)} data-testid="fp-daily-today-btn"
          className="text-xs text-amber-300 hover:text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-2.5 py-1.5">
          Today
        </button>
        <div className="flex-1" />
        <button onClick={fetchData} title="Refresh" className="text-zinc-500 hover:text-zinc-200 p-1.5">
          <RotateCw size={13} />
        </button>
      </div>

      {/* Summary */}
      {data.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Kpi l="SKUs planned"      v={data.summary.products}                       tone="text-zinc-100" />
          <Kpi l="Total Left to Make" v={fmtQty(data.summary.total_left_to_make)}    tone="text-red-400" />
          <Kpi l="Total Produced"     v={fmtQty(data.summary.total_produced)}         tone="text-emerald-400" />
          <Kpi l="Total Dispatched"   v={fmtQty(data.summary.total_dispatched)}       tone="text-blue-400" />
        </div>
      )}

      {/* Grid */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="text-xs w-full" data-testid="fp-daily-grid-table">
            <thead className="sticky top-0 bg-zinc-900 z-10">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] uppercase font-semibold text-zinc-400 tracking-wider sticky left-0 bg-zinc-900 z-20 min-w-[180px] border-r border-zinc-700 border-b">
                  SKU
                </th>
                {(data.dates || []).map(d => {
                  const { wk, dm, isToday } = dayLabel(d, todayIso);
                  return (
                    <th key={d} className={`px-2 py-2 text-center text-[10px] uppercase font-semibold whitespace-nowrap border-b border-zinc-700 ${isToday ? 'bg-amber-500/10 text-amber-400' : 'text-zinc-400'}`}>
                      <div>{wk}</div>
                      <div className="text-zinc-500 font-normal">{dm}</div>
                    </th>
                  );
                })}
                <th className="px-2 py-2 text-right text-[10px] uppercase font-semibold text-red-400 tracking-wider whitespace-nowrap border-b border-zinc-700 bg-zinc-900">
                  🔴 Left
                </th>
                <th className="px-2 py-2 text-right text-[10px] uppercase font-semibold text-emerald-400 tracking-wider whitespace-nowrap border-b border-zinc-700 bg-zinc-900">
                  🟢 Made
                </th>
                <th className="px-2 py-2 text-right text-[10px] uppercase font-semibold text-blue-400 tracking-wider whitespace-nowrap border-b border-zinc-700 bg-zinc-900">
                  🔵 Dispatched
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={(data.dates?.length || 0) + 4} className="text-center py-8 text-zinc-500">Loading…</td></tr>}
              {!loading && (data.rows || []).length === 0 && (
                <tr><td colSpan={(data.dates?.length || 0) + 4} className="text-center py-10 text-zinc-600">No plans in this window. Use &ldquo;+ AI Add Production&rdquo; to plan.</td></tr>
              )}
              {!loading && (data.rows || []).map((row, idx) => (
                <tr key={row.product_id + '-' + idx} className="border-b border-zinc-800/60 hover:bg-zinc-800/20"
                    data-testid={`fp-daily-row-${row.product_id || row.product_name}`}>
                  <td className="px-3 py-2 text-zinc-100 sticky left-0 bg-zinc-900 z-[1] border-r border-zinc-800/60 min-w-[180px]">
                    <p className="font-medium text-sm">{row.product_name}</p>
                    <p className="text-[10px] text-zinc-500">Total planned: {fmtQty(row.totals.planned, row.unit)}</p>
                  </td>
                  {(data.dates || []).map(d => {
                    const cell = row.cells[d] || { planned: 0, produced: 0, dispatched: 0 };
                    const left = Math.max(0, cell.planned - cell.produced);
                    const isToday = d === todayIso;
                    if (cell.planned === 0 && cell.produced === 0 && cell.dispatched === 0) {
                      return (
                        <td key={d} className={`px-2 py-2 text-center text-zinc-700 ${isToday ? 'bg-amber-500/5' : ''}`}>—</td>
                      );
                    }
                    return (
                      <td key={d} className={`px-2 py-2 text-center whitespace-nowrap ${isToday ? 'bg-amber-500/5' : ''}`}>
                        <div className="flex flex-col items-center gap-0.5">
                          {left > 0.001 && (
                            <span className="text-red-300 bg-red-500/10 rounded px-1.5 py-0.5 text-[11px] font-mono font-semibold">
                              🔴 {fmtQty(left)}
                            </span>
                          )}
                          {cell.produced > 0.001 && (
                            <span className="text-emerald-300 bg-emerald-500/10 rounded px-1.5 py-0.5 text-[11px] font-mono font-semibold">
                              🟢 {fmtQty(cell.produced)}
                            </span>
                          )}
                          {cell.dispatched > 0.001 && (
                            <span className="text-blue-300 bg-blue-500/10 rounded px-1.5 py-0.5 text-[11px] font-mono font-semibold">
                              🔵 {fmtQty(cell.dispatched)}
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 text-right font-mono text-sm text-red-300 bg-red-500/[0.03]">
                    {row.totals.left_to_make > 0.001 ? fmtQty(row.totals.left_to_make, row.unit) : '—'}
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-sm text-emerald-300 bg-emerald-500/[0.03]">
                    {row.totals.available_made > 0.001 ? fmtQty(row.totals.available_made, row.unit) : '—'}
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-sm text-blue-300 bg-blue-500/[0.03]">
                    {row.totals.dispatched > 0.001 ? fmtQty(row.totals.dispatched, row.unit) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Kpi({ l, v, tone }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-2.5">
      <p className="text-[10px] uppercase text-zinc-500 tracking-wider">{l}</p>
      <p className={`text-lg font-heading font-bold ${tone}`}>{v}</p>
    </div>
  );
}
